import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { getAvailableActions } from './utils/permissionCatalog';
import { checkPermissionActions } from './utils/permissions';

function AdminOverviewIcon({ src, alt }: { src: string; alt: string }) {
  return <img className="admin-overview-card-icon-img" src={src} alt={alt} />;
}

type AdminCardProps = {
  title: string;
  description: string;
  details: string[];
  to: string;
  icon: React.ReactNode;
  testId: string;
};

function AdminCard({ title, description, details, to, icon, testId }: AdminCardProps) {
  const navigate = useNavigate();

  const go = () => navigate(to);

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      go();
    }
  };

  return (
    <div
      className="card admin-overview-card"
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={onKeyDown}
      data-testid={testId}
    >
      <div className="admin-overview-card-header">
        <span className="admin-overview-card-icon" aria-hidden="true">{icon}</span>
        <div className="admin-overview-card-titles">
          <div className="admin-overview-card-title">{title}</div>
          <div className="muted admin-overview-card-subtitle">{description}</div>
        </div>
        <button
          type="button"
          className="admin-overview-card-arrow"
          aria-label={`Open ${title}`}
          onClick={(e) => {
            e.stopPropagation();
            go();
          }}
        >
          <img
            className="admin-overview-card-arrow-icon"
            src="/icons/arrow-right.svg"
            alt=""
          />
        </button>
      </div>

      <div className="admin-overview-card-details">
        {details.map((text) => (
          <div key={text} className="admin-overview-card-detail">{text}</div>
        ))}
      </div>
    </div>
  );
}

export default function AdminOverviewPage({ currentUser }: { currentUser?: any }) {
  const actions = getAvailableActions(currentUser);
  const hasAnyAction = (...requestedActions: string[]) =>
    checkPermissionActions(actions, requestedActions);

  return (
    <div className="stack" data-testid="page-admin">
      <PageHeader title="Administration" subtitle="Set up and manage your organization" />

      <div className="grid two-col admin-overview-grid">
        <AdminCard
          title="Organization Management"
          description="Set up or edit your organization"
          details={["Add Organization", "Edit Organization"]}
          to="/organizations"
          icon={<AdminOverviewIcon src="/icons/organization-management.svg" alt="" />}
          testId="admin-card-organizations"
        />

              {hasAnyAction(
                'ReadUserManagement',
                'WriteUserManagement',
                'AdminUserManagement',
              ) && <AdminCard
          title="User Management"
          description="Set up administrators, users, and configure user permissions"
          details={["User Management", "Add Administrators", "Configure Permissions"]}
          to="/users"
          icon={<AdminOverviewIcon src="/icons/user-management.svg" alt="" />}
          testId="admin-card-users"
        />}

        {hasAnyAction('ReadBrands', 'WriteBrands', 'AdminBrands') && <AdminCard
          title="Brand Management"
          description="Add brands, configure details, and upload brand media assets"
          details={["Configure Brand Page", "Add Brand Details", "Upload Brand Media"]}
          to="/brands"
          icon={<AdminOverviewIcon src="/icons/brand-management.svg" alt="" />}
          testId="admin-card-brands"
        />}

        {hasAnyAction('ReadFacilities', 'WriteFacilities', 'AdminFacilities') && <AdminCard
          title="Facility Management"
          description="Set up facilities and view import history"
          details={["Add Facilities & Manufacturers", "Configure Permissions"]}
          to="/imports?tab=facilities"
          icon={<AdminOverviewIcon src="/icons/facility-management.svg" alt="" />}
          testId="admin-card-integrations"
        />}
      </div>
    </div>
  );
}
